import { createContext, useContext, useEffect, useState, useRef, type ReactNode } from "react";

interface HeadyContext {
  userId: string;
  teamId: string;
  toolSlug?: string;
  hasOwnKeys?: boolean;
}

interface SDKState {
  sdk: any | null;
  context: HeadyContext | null;
  brandGuide: any | null;
  ready: boolean;
}

const SDKContext = createContext<SDKState>({
  sdk: null, context: null, brandGuide: null, ready: false,
});

export function SDKProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SDKState>({
    sdk: null, context: null, brandGuide: null, ready: false,
  });
  const initRef = useRef(false);
  const sdkRef = useRef<any>(null);

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    let polling: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    // Armed UNCONDITIONALLY, and before anything else: the app must paint even
    // if the parent SDK script never loads.
    //
    // This timer used to live INSIDE the `if (!window.HeadySDK)` guard below,
    // which made it unreachable in the one case it existed for. index.html
    // pulls heady-sdk.js from another origin (os.useheady.com); if that request
    // is blocked, 404s, or is simply slow past the poll, `window.HeadySDK`
    // never appears, tryInit() re-queues itself every 100ms forever, and
    // `ready` stays false for good. Shell's `if (!ready) return <div>Loading…`
    // then swallows the ENTIRE app — nav included — so a third-party script
    // failure presented as "the whole product is blank".
    const fallback = setTimeout(() => {
      if (cancelled) return;
      setState((prev) =>
        prev.ready ? prev : { sdk: sdkRef.current, context: null, brandGuide: null, ready: true }
      );
    }, 2000);

    function tryInit() {
      if (cancelled) return;
      const W = window as any;
      if (!W.HeadySDK) {
        polling = setTimeout(tryInit, 100);
        return;
      }
      const sdk = new W.HeadySDK();
      sdkRef.current = sdk;
      // If the SDK arrives after the fallback already fired, attach it so
      // sdk.* calls still work; `ready` is left alone.
      setState((prev) => (prev.sdk ? prev : { ...prev, sdk }));
      sdk.on("ready", async (ctx: HeadyContext) => {
        let brandGuide = null;
        try { brandGuide = await sdk.getBrandGuide(); } catch {}
        if (!cancelled) setState({ sdk, context: ctx, brandGuide, ready: true });
      });
    }
    tryInit();

    return () => {
      cancelled = true;
      clearTimeout(fallback);
      if (polling) clearTimeout(polling);
    };
  }, []);

  return <SDKContext.Provider value={state}>{children}</SDKContext.Provider>;
}

export function useSDK() { return useContext(SDKContext); }
